import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import { adminBulkRbacService } from '../services'
import { hasAdminPermission, PERMISSION_SUBJECTS } from '../utils/adminPermissions'
import useAuthStore from '../store/authStore'

const BulkRoleAssignmentPage = () => {
  const adminPermissions = useAuthStore((s) => s.adminPermissions)
  const canWrite = hasAdminPermission(
    adminPermissions,
    PERMISSION_SUBJECTS.BULK_ROLE_ASSIGNMENT,
    'write',
  )

  const [file, setFile] = useState(null)
  const [loading, setLoading] = useState(false)
  const [rolesLoading, setRolesLoading] = useState(true)
  const [roles, setRoles] = useState([])
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)

  const loadRoles = useCallback(async () => {
    try {
      setRolesLoading(true)
      const data = await adminBulkRbacService.listRolesReference()
      setRoles(Array.isArray(data) ? data : [])
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to load roles')
    } finally {
      setRolesLoading(false)
    }
  }, [])

  useEffect(() => {
    loadRoles()
  }, [loadRoles])

  const onSubmit = async () => {
    if (!file) {
      setError('Choose a CSV file first')
      return
    }
    setLoading(true)
    setError(null)
    setResult(null)
    try {
      const data = await adminBulkRbacService.importRoleAssignments(file)
      setResult(data)
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Import failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Typography variant="h5" gutterBottom>
        Bulk role assignment
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Upload CSV with columns <strong>email</strong> and <strong>role</strong>. Roles are merged
        onto each user&apos;s existing assignments.
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <Button variant="outlined" component="label">
              Choose CSV
              <input
                type="file"
                accept=".csv,text/csv"
                hidden
                onChange={(e) => {
                  setFile(e.target.files?.[0] || null)
                  setResult(null)
                }}
              />
            </Button>
            <Typography variant="body2" color="text.secondary">
              {file ? file.name : 'No file selected'}
            </Typography>
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
              disabled={!canWrite || !file || loading}
              onClick={onSubmit}
            >
              Import
            </Button>
          </Box>
          {!canWrite ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              You need bulk role assignment write permission to import.
            </Alert>
          ) : null}
        </CardContent>
      </Card>

      {result?.summary ? (
        <Alert severity={result.summary.failedRows ? 'warning' : 'success'} sx={{ mb: 2 }}>
          {result.summary.successRows} of {result.summary.totalRows} rows succeeded;{' '}
          {result.summary.usersUpdated} user(s) updated.
        </Alert>
      ) : null}

      {result?.rows?.length ? (
        <Paper sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ p: 2, pb: 0 }}>
            Import results
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Row</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {result.rows.map((row) => (
                  <TableRow key={`${row.row}-${row.email}-${row.role}`}>
                    <TableCell>{row.row}</TableCell>
                    <TableCell>{row.email}</TableCell>
                    <TableCell>{row.role}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={row.status}
                        color={row.status === 'success' ? 'success' : 'error'}
                      />
                    </TableCell>
                    <TableCell>{row.reason || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : null}

      <Typography variant="h6" gutterBottom>
        Role reference
      </Typography>
      {rolesLoading ? (
        <CircularProgress size={28} />
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Display name</TableCell>
                <TableCell>System</TableCell>
                <TableCell>Assignments</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {roles.map((role) => (
                <TableRow key={role.id || role.code}>
                  <TableCell>{role.code}</TableCell>
                  <TableCell>{role.display_name}</TableCell>
                  <TableCell>{role.is_system ? 'Yes' : 'No'}</TableCell>
                  <TableCell>{role.assignment_count ?? 0}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  )
}

export default BulkRoleAssignmentPage
